/*
  Lenis smooth scrolling.

  Lenis keeps the page on the real window scroll - it eases the position it
  writes rather than transforming a wrapper - so everything already listening
  to `scroll` (the sticky header, ScrollTrigger, Shopify's own components)
  keeps working untouched. That is why there is no wrapper/content markup here.

  Three things need wiring beyond `new Lenis()`:

  1. GSAP drives the loop. Two independent rAF loops running a frame apart is
     what makes scrub animations look like they lag the scroll, so Lenis is
     ticked from gsap's ticker and lag smoothing is off - lag smoothing lies
     about elapsed time after a stall, which shows up as a jump.
  2. Overlays stop it. Drawers and modals lock the page with an
     `overflow-hidden` class on <body>; Lenis has to be told, or its wheel
     handler keeps easing the locked page behind the overlay.
  3. Inner scrollers opt out. Lenis swallows wheel events for the whole page,
     which would leave scrollable panels (drawer contents, the country list,
     mega menus) dead under the wheel. `allowNestedScroll` hands those back.
*/
(function () {
  if (typeof Lenis !== 'function') return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var lenis = new Lenis({
    /*
      Feel. `lerp` is the exponential-catch-up mode: every frame the page
      closes a fixed fraction of the gap to where the wheel has asked it to be.
      Lenis normalises it by frame time (`1 - e^(-60 * lerp * dt)` internally),
      so this is a real time constant rather than a per-frame step - 144Hz and
      60Hz settle in the same number of milliseconds.

      0.15 puts the time constant near 110ms and finishes inside ~250ms, which
      is about as tight as smoothing gets while still reading as smooth. The
      `duration` mode this replaced restarted a 1.05s eased tween on every
      wheel event, so the page kept gliding long after the wheel stopped and
      each new notch fought the tween already running - which is what felt
      slow. Lower this number for more glide, raise it toward native.
    */
    lerp: 0.15,
    /*
      Touch is left native. Phone scrolling is already smooth and already
      matches what every other app on the device does; easing it on top adds
      latency to the one gesture people are most sensitive about.
    */
    smoothWheel: true,
    syncTouch: false,
    /*
      Lenis finds overflowing ancestors of whatever the cursor is over and
      leaves their wheel events alone, so drawer contents, the country list and
      the mega menus scroll natively - and hand the page back once they hit
      their own top or bottom, the way nested scrolling normally behaves.
      Detecting them beats listing selectors that would rot as sections change;
      `data-lenis-prevent` is still there for anything measurement misses.
    */
    allowNestedScroll: true,
    anchors: true,
  });

  window.lenis = lenis;

  if (window.gsap) {
    /*
      The third argument prioritises this callback, putting it at the head of
      the ticker queue. That ordering is the whole point: ScrollTrigger reads
      the scroll position on the same tick, so if Lenis wrote it second every
      pinned and scrubbed section would render one frame behind the scroll.

      There is deliberately no `lenis.on('scroll', ScrollTrigger.update)` here.
      That line belongs to the wrapper/transform setup, where the real scroll
      position never moves and ScrollTrigger has to be told by hand. This page
      scrolls natively, so ScrollTrigger's own scroll listener already sees
      every change - calling update again just ran the trigger maths twice per
      frame, on top of the pins and scrubs it was already paying for.
    */
    window.gsap.ticker.add(function (time) {
      // gsap's ticker is in seconds, Lenis expects milliseconds.
      lenis.raf(time * 1000);
    }, false, true);

    /*
      Lag smoothing rewrites the clock after a stalled frame to keep tweens
      looking continuous. Scroll-driven motion wants the opposite: the true
      elapsed time, so the page lands exactly where the wheel left it instead
      of catching up in a jump.
    */
    window.gsap.ticker.lagSmoothing(0);

    /*
      Mobile browsers fire resize when the URL bar slides away, and a
      ScrollTrigger refresh re-measures every trigger on the page. Height-only
      resizes are exactly that case, and skipping them is what stops the
      hitch mid-scroll on phones.
    */
    if (window.ScrollTrigger) {
      window.ScrollTrigger.config({ ignoreMobileResize: true });
    }
  } else {
    var raf = function (time) {
      lenis.raf(time);
      window.requestAnimationFrame(raf);
    };
    window.requestAnimationFrame(raf);
  }

  /*
    Every overlay in the theme locks the page the same way: an `overflow-hidden`
    class on <body>. Watching the attribute covers all of them - cart drawer,
    menu drawer, modals, pickup availability - without each one needing to know
    Lenis exists.

    What it can't do is read the class name and call it locked. Several of those
    classes are breakpoint-scoped (`overflow-hidden-mobile` releases at 750px,
    `-tablet` at 990px), so the class being present doesn't mean the page is
    held - the header drawer leaves `-tablet` on at desktop widths, and stopping
    Lenis there would just kill scrolling. Asking for the resolved overflow
    lets the browser answer with the media queries already applied, so the
    breakpoints live in base.css only and can move without this file knowing.

    Lenis' own `lenis-stopped` overflow lands on <html>, not <body>, so reading
    <body> can't feed Lenis its own state back.
  */
  function syncLockState() {
    var locked = window.getComputedStyle(document.body).overflowY === 'hidden';
    if (locked === lenis.isStopped) return;
    if (locked) {
      lenis.stop();
    } else {
      lenis.start();
    }
  }

  /*
    getComputedStyle flushes pending style, so calling it straight off the
    resize event pays for a recalc on every one of the burst a window drag
    fires - and on phones, on every pixel of the URL bar sliding away, mid
    scroll. One check per frame is as often as the answer can change anyway.
  */
  var lockCheckPending = false;
  function scheduleLockCheck() {
    if (lockCheckPending) return;
    lockCheckPending = true;
    window.requestAnimationFrame(function () {
      lockCheckPending = false;
      syncLockState();
    });
  }

  syncLockState();
  new MutationObserver(syncLockState).observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });
  // Crossing a breakpoint with an overlay open changes the lock without
  // touching the class, and fires no mutation.
  window.addEventListener('resize', scheduleLockCheck, { passive: true });
})();
